import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

/**
 * A mapping from object keys -> column labels
 */
const columnLabels = {
  inspectionId: 'Inspection ID',
  deviceId: 'Device ID',
  test: 'Test Label',
  taskType: 'Task Type',
  approvalDate: 'Approval Date',
  lastDate: 'Last Date',
  nextDate: 'Next Date',
  inspYear: 'Year Started',
};

/**
 * Each field can be (editable or not) with a type
 */
const fieldDefinitions = [
  { key: 'inspectionId', editable: false, type: 'text' },
  { key: 'deviceId', editable: false, type: 'text' },
  { key: 'test', editable: false }, // read-only
  { key: 'taskType', editable: true, type: 'text' },
  { key: 'approvalDate', editable: true, type: 'date' },
  { key: 'lastDate', editable: true, type: 'date' },
  { key: 'nextDate', editable: true, type: 'date' },
  { key: 'inspYear', editable: true, type: 'number' },
];

/**
 * BuildingInspectionTable
 * - If isEditMode=false, we show a simple read-only table
 * - If isEditMode=true, we show edit/delete icons, row-level editing, etc.
 */
export default function BuildingInspectionTable({
  assignedInspections,
  setAssignedInspections,
  onUpdateInspection,
  onRemoveInspection,
  isEditMode,
}) {
  // In read-only mode, we skip row editing
  if (!isEditMode) {
    return (
      <div className="mb-4 overflow-x-auto" style={{ maxHeight: '400px' }}>
        <table className="w-full bg-white rounded-lg overflow-hidden shadow-lg border-2 border-gray-300">
          <thead className="bg-gray-200">
            <tr>
              {Object.keys(columnLabels).map((colKey) => (
                <th
                  key={colKey}
                  className="px-4 py-2 border text-left font-bold text-gray-700"
                >
                  {columnLabels[colKey]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-gray-800">
            {assignedInspections.map((insp, idx) => (
              <tr key={insp.inspectionId || idx} className="border-t hover:bg-gray-50">
                {Object.keys(columnLabels).map((colKey) => {
                  const val = insp[colKey] ?? 'N/A';
                  return (
                    <td key={colKey} className="px-4 py-2 border bg-white">
                      {val}
                    </td>
                  );
                })}
              </tr>
            ))}
            {assignedInspections.length === 0 && (
              <tr>
                <td
                  colSpan={Object.keys(columnLabels).length}
                  className="text-center py-4"
                >
                  No inspections found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  // Edit mode => show row-level editing
  const handleEditRow = (index) => {
    setAssignedInspections((prev) =>
      prev.map((row, i) => (i === index ? { ...row, isEditing: true } : row))
    );
  };

  const handleCancelRow = (index) => {
    setAssignedInspections((prev) =>
      prev.map((row, i) => (i === index ? { ...row, isEditing: false } : row))
    );
  };

  const handleFieldChange = (e, index) => {
    const { name, value } = e.target;
    setAssignedInspections((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [name]: value } : row))
    );
  };

  const handleSaveRow = (index) => {
    const rowData = assignedInspections[index];
    if (onUpdateInspection) {
      onUpdateInspection(rowData.inspectionId, rowData);
    }
    handleCancelRow(index);
  };

  const handleDeleteRow = (index) => {
    const rowData = assignedInspections[index];
    if (onRemoveInspection) {
      onRemoveInspection(rowData.inspectionId);
    }
  };

  return (
    <div className="mb-4 overflow-x-auto" style={{ maxHeight: '400px' }}>
      <table className="w-full bg-white rounded-lg overflow-hidden shadow-lg border-2 border-gray-300">
        <thead className="bg-gray-200">
          <tr>
            <th className="p-2 border text-left"></th> {/* Edit col */}
            {Object.keys(columnLabels).map((colKey) => (
              <th
                key={colKey}
                className="px-4 py-2 border text-left font-bold text-gray-700"
              >
                {columnLabels[colKey]}
              </th>
            ))}
            <th className="p-2 border text-left"></th> {/* Delete col */}
          </tr>
        </thead>
        <tbody>
          {assignedInspections.map((insp, index) => (
            <tr key={insp.inspectionId || index} className="border-t hover:bg-gray-50">
              {/* Left-most cell => Edit/Save/Cancel */}
              <td className="p-2 border bg-white text-center">
                {insp.isEditing ? (
                  <>
                    <button
                      onClick={() => handleSaveRow(index)}
                      className="text-green-500 hover:bg-gray-300 rounded-full mr-1"
                      title="Save"
                    >
                      <CheckIcon />
                    </button>
                    <button
                      onClick={() => handleCancelRow(index)}
                      className="text-red-500 hover:bg-gray-300 rounded-full"
                      title="Cancel"
                    >
                      <CloseIcon />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleEditRow(index)}
                    className="border-gray-200 hover:bg-gray-100 rounded-xl border-2 p-2"
                    title="Edit"
                  >
                    <EditIcon />
                  </button>
                )}
              </td>

              {/* Main columns */}
              {Object.keys(columnLabels).map((colKey) => {
                // find definition for this field
                const def = fieldDefinitions.find((d) => d.key === colKey);
                const canEdit = def?.editable;
                const inputType = def?.type || 'text';

                if (!insp.isEditing || !canEdit) {
                  // read-only
                  const val = insp[colKey] ?? 'N/A';
                  return (
                    <td key={colKey} className="px-4 py-2 border bg-white">
                      {val}
                    </td>
                  );
                }

                // If editing + canEdit => input
                return (
                  <td key={colKey} className="px-4 py-2 border bg-white">
                    <input
                      name={colKey}
                      type={inputType}
                      value={insp[colKey] || ''}
                      onChange={(e) => handleFieldChange(e, index)}
                      className="border p-1 rounded w-full"
                    />
                  </td>
                );
              })}

              {/* Right-most cell => Delete */}
              <td className="p-2 border bg-white text-center">
                <button
                  onClick={() => handleDeleteRow(index)}
                  className="bg-red-400 hover:bg-red-500 text-white rounded-xl border-2 p-2"
                  title="Delete"
                >
                  <DeleteIcon />
                </button>
              </td>
            </tr>
          ))}
          {assignedInspections.length === 0 && (
            <tr>
              <td
                colSpan={Object.keys(columnLabels).length + 2}
                className="text-center py-4"
              >
                No inspections found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
